// The greeting a page says to the person: the time of day and their first
// name. One rule, so the Home page, the landing page, the page helpers and
// the Help pane cannot drift apart (Adam, 19 Oct 2026).

import useAccount from '../hooks/useAccount'

/** "Good morning" before 12, "Good afternoon" before 17, else "Good evening", by the browser's clock. */
export function timeOfDayGreeting(now: Date = new Date()): string {
  const hour = now.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

const capitalise = (w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()

/**
 * The person's first name from their sign-in: the licensing user name when it
 * is a name and not the email again (the rule realm_admins.go applies to
 * name a person), else the email's local part up to its first dot, dash,
 * underscore or plus ("adam.knight@…" is "Adam"). '' when neither gives a
 * word that starts with a letter: the greeting then names nobody.
 */
export function firstNameFor(info?: { name?: string | null; email?: string | null } | null): string {
  const name = info?.name?.trim() ?? ''
  if (name && !name.includes('@')) {
    const first = name.split(/\s+/)[0]
    if (/^\p{L}/u.test(first)) return first
  }
  const email = info?.email?.trim() || (name.includes('@') ? name : '')
  const local = email.split('@')[0]?.split(/[._+-]/)[0] ?? ''
  return /^\p{L}[\p{L}']*$/u.test(local) ? capitalise(local) : ''
}

/** The signed-in person's first name, or '' (firstNameFor). */
export function useFirstName(): string {
  const account = useAccount()
  return firstNameFor(account.user?.session_info)
}

/** "Good morning, Adam." or, with no name, "Good morning." */
export function helloLine(greeting: string, firstName: string): string {
  return firstName ? `${greeting}, ${firstName}.` : `${greeting}.`
}

/**
 * What an empty helper or Help pane says before the first message. With
 * rows of the page's own above the composer (the Tables summary), the hello
 * stands alone at the top and the section's opening line follows those rows
 * as the question that invites a follow-up; otherwise both are one line at
 * the top.
 */
export function paneOpening({ greeting, firstName, intro, afterIntroRows }: {
  greeting: string
  firstName: string
  intro: string
  afterIntroRows: boolean
}): { top: string; after?: string } {
  const hello = helloLine(greeting, firstName)
  if (!intro) return { top: hello }
  return afterIntroRows ? { top: hello, after: intro } : { top: `${hello} ${intro}` }
}
